import React, { useState } from 'react';

const STATUS_CONFIG = {
  green: { label: 'All good', color: '#10b981', bg: '#d1fae5', icon: '🟢' },
  amber: { label: 'Watch this', color: '#f59e0b', bg: '#fef3c7', icon: '🟡' },
  red:   { label: 'Act now', color: '#ef4444', bg: '#fee2e2', icon: '🔴' },
  grey:  { label: 'Gone quiet', color: '#6b7280', bg: '#f3f4f6', icon: '⚫' },
};

function TrendArrow({ current, previous }) {
  if (current > previous) return <span className="trend up">↑</span>;
  if (current < previous) return <span className="trend down">↓</span>;
  return <span className="trend flat">→</span>;
}

function Sparkline({ counts, isFallingOff, trend }) {
  // counts = array of 8 weekly job counts, oldest first
  const weeks = counts || [];
  const max = Math.max(...weeks, 1);
  const color = isFallingOff ? '#ef4444' : trend === 'up' ? '#10b981' : trend === 'down' ? '#f59e0b' : '#0e7c7b';
  return (
    <div className="sparkline">
      {weeks.map((v, i) => (
        <div key={i} className="spark-col">
          <div
            className="spark-bar"
            style={{
              height: `${Math.max(3, (v / max) * 28)}px`,
              background: i >= 6 ? color : '#cbd5e1',
              opacity: i < 4 ? 0.5 : 1,
            }}
          />
          {i === 7 && <span className="spark-num">{v}</span>}
        </div>
      ))}
      {isFallingOff && <span className="spark-warning">↓ dropping</span>}
    </div>
  );
}

function WeekDots({ thisWeek, lastWeek, weekBefore }) {
  const dots = [weekBefore, lastWeek, thisWeek];
  const max = Math.max(...dots, 1);
  return (
    <div className="week-dots">
      {dots.map((v, i) => (
        <div key={i} className="week-dot-col">
          <div
            className="week-dot-bar"
            style={{
              height: `${Math.max(4, (v / max) * 24)}px`,
              background: i === 2 ? '#0e7c7b' : '#cbd5e1',
            }}
          />
          <span className="week-dot-num">{v}</span>
        </div>
      ))}
    </div>
  );
}

function ClientCard({ client, sparkline, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[client.status];

  return (
    <div
      className={`client-card status-${client.status}`}
      style={{ borderLeftColor: cfg.color }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="card-header">
        <div className="card-title-row">
          <span className="status-icon">{cfg.icon}</span>
          <span className="client-name">{client.name}</span>
          {client.responsible && (
            <span className="responsible-tag">{client.responsible}</span>
          )}
        </div>
        <div className="card-meta">
          {client.daysSinceLastJob !== null && (
            <span className={`days-ago ${client.daysSinceLastJob > 30 ? 'warn' : ''}`}>
              Last job: {client.daysSinceLastJob === 0 ? 'today' : `${client.daysSinceLastJob}d ago`}
            </span>
          )}
          {client.openJobs > 0 && (
            <span className="open-jobs-tag">
              {client.openJobs} open · AED {(client.openJobsValue / 1000).toFixed(0)}K
            </span>
          )}
        </div>
      </div>

      <div className="card-body">
        <div className="card-stats">
          <div className="stat-group">
            {sparkline ? (
              <Sparkline
                counts={sparkline.weekCounts}
                isFallingOff={sparkline.isFallingOff}
                trend={sparkline.trend}
              />
            ) : (
              <WeekDots
                thisWeek={client.thisWeek}
                lastWeek={client.lastWeek}
                weekBefore={client.weekBefore}
              />
            )}
            <span className="stat-label">Jobs / week{sparkline ? ' (8wk)' : ''}</span>
          </div>

          {client.arTotal > 0 && (
            <div className="stat-group">
              <span className={`stat-value ${client.arCritical > 0 ? 'danger' : client.arOverdue > 0 ? 'warn' : ''}`}>
                AED {(client.arTotal / 1000).toFixed(0)}K
              </span>
              <span className="stat-label">Outstanding AR</span>
              {client.arCritical > 0 && (
                <span className="ar-critical">AED {(client.arCritical / 1000).toFixed(0)}K 90+ days</span>
              )}
            </div>
          )}

          {client.gpPct !== null && (
            <div className="stat-group">
              <span className={`stat-value ${client.gpPct >= 20 ? 'good' : client.gpPct >= 15 ? 'warn' : 'danger'}`}>
                {client.gpPct.toFixed(1)}%
              </span>
              <span className="stat-label">GP%</span>
            </div>
          )}
        </div>

        {/* Flags */}
        {client.flags.length > 0 && (
          <div className="card-flags">
            {client.flags.map((f, i) => (
              <div key={i} className={`inline-flag flag-${f.severity}`}>
                <span className="flag-msg">{f.message}</span>
                <span className="flag-action">→ {f.action}</span>
              </div>
            ))}
          </div>
        )}

        {/* All good message */}
        {client.flags.length === 0 && client.status === 'green' && (
          <div className="all-good">
            ✓ Active · GP healthy · AR current — no action needed
          </div>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="card-expanded" onClick={e => e.stopPropagation()}>
          <div className="expanded-grid">
            <div className="exp-item">
              <span className="exp-label">Total jobs in file</span>
              <span className="exp-value">{client.totalJobs}</span>
            </div>
            <div className="exp-item">
              <span className="exp-label">Completed jobs</span>
              <span className="exp-value">{client.completedJobs}</span>
            </div>
            <div className="exp-item">
              <span className="exp-label">Total revenue</span>
              <span className="exp-value">AED {client.totalRev.toLocaleString('en', { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="exp-item">
              <span className="exp-label">Total P&L</span>
              <span className={`exp-value ${client.totalPL >= 0 ? 'good' : 'danger'}`}>
                AED {client.totalPL.toLocaleString('en', { maximumFractionDigits: 0 })}
              </span>
            </div>
            {client.staleJobs > 0 && (
              <div className="exp-item warn">
                <span className="exp-label">Stale open jobs (60+ days)</span>
                <span className="exp-value">{client.staleJobs}</span>
              </div>
            )}
          </div>
          {client.notes && (
            <div className="exp-notes">{client.notes}</div>
          )}
          <div className="exp-actions">
            <button
              className="btn-sm"
              onClick={() => {
                const note = prompt('Add note for this client:', client.notes || '');
                if (note !== null) onUpdate(client.name, { notes: note });
              }}
            >
              Add Note
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                const person = prompt('Responsible person:', client.responsible || '');
                if (person !== null) onUpdate(client.name, { responsible: person });
              }}
            >
              Set Owner
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientPulse({ clients, weeklyStats, sparklines, entity, onUpdateClient }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = clients.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    red: clients.filter(c => c.status === 'red').length,
    amber: clients.filter(c => c.status === 'amber').length,
    grey: clients.filter(c => c.status === 'grey').length,
    green: clients.filter(c => c.status === 'green').length,
  };

  // This week summary
  const thisWeek = weeklyStats[weeklyStats.length - 1] || {};
  const lastWeek = weeklyStats[weeklyStats.length - 2] || {};

  return (
    <div className="view client-pulse">
      {/* Summary row */}
      <div className="pulse-summary">
        <div className="summary-card">
          <span className="sum-value">{thisWeek.opened || 0}</span>
          <span className="sum-label">Jobs opened this week</span>
          <TrendArrow current={thisWeek.opened} previous={lastWeek.opened} />
        </div>
        <div className="summary-card">
          <span className="sum-value">{thisWeek.closed || 0}</span>
          <span className="sum-label">Jobs closed this week</span>
          <TrendArrow current={thisWeek.closed} previous={lastWeek.closed} />
        </div>
        <div className="summary-card">
          <span className="sum-value">{clients.filter(c => c.openJobs > 0).length}</span>
          <span className="sum-label">Clients with open jobs</span>
        </div>
        <div className={`summary-card ${counts.red > 0 ? 'alert' : ''}`}>
          <span className="sum-value">{counts.red + counts.amber}</span>
          <span className="sum-label">Clients needing attention</span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <input
          className="search-input"
          placeholder="Search client..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="filter-pills">
          {[
            { id: 'all', label: `All (${clients.length})` },
            { id: 'red', label: `🔴 Act now (${counts.red})` },
            { id: 'amber', label: `🟡 Watch (${counts.amber})` },
            { id: 'grey', label: `⚫ Gone quiet (${counts.grey})` },
            { id: 'green', label: `🟢 Fine (${counts.green})` },
          ].map(f => (
            <button
              key={f.id}
              className={`pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Client cards */}
      <div className="client-cards">
        {filtered.length === 0 ? (
          <div className="no-results">No clients match this filter.</div>
        ) : (
          filtered.map(client => (
            <ClientCard
              key={client.name}
              client={client}
              sparkline={sparklines ? sparklines[client.name] : null}
              onUpdate={onUpdateClient}
            />
          ))
        )}
      </div>
    </div>
  );
}
